import React, { useEffect, useState } from "react";
import CardDataStats from "../../components/CardDataStats/CardDataStats.tsx";
import PremiumsChart from "../../components/Charts/PremiumsChart.tsx";
import OfficeService from "../../services/OfficeService/OfficeService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import {
  normalizeDate,
  normalizeSummaryReportProperties,
} from "../../utils/NormalizationUtil.ts";
import moment from "moment/moment";
import CapitalizationUtil from "../../utils/CapitalizationUtil.ts";
import NewContractsChart from "../../components/Charts/NewContractsChart.tsx";

interface SummaryDetails {
  count: string;
  newContracts: string;
  time: string;
}

const SUMMARY_MAPPING_ENUM: Record<keyof SummaryDetails, string> = {
  count: "Брой вноски",
  newContracts: "Нови договори",
  time: "Време",
};

const SUMMARY_ICON_MAPPING_ENUM: Record<keyof SummaryDetails, string> = {
  count: "fa fa-calculator",
  newContracts: "fa-solid fa-file-contract",
  time: "fa fa-time",
};

export interface ChartState {
  series: { name: string; data: number[] }[];
  categories?: string[];
  timePeriods?: string[];
}

export interface YAxisConfig {
  max: number;
  tickAmount: number;
  stepSize: number;
}

interface ChartRow {
  premium: string | number;
  newContracts: string | number;
  period: string; // e.g. "20261001 - 20261008"
}

/**
 * Computes a clean Y-axis config from an array of numeric values.
 *  - Round the max up to a "nice" number (nearest power-of-10 step).
 *  - Use 10 ticks so the step labels are round and easy to read.
 */
const computeYAxisConfig = (values: number[]): YAxisConfig => {
  const rawMax = Math.max(...values, 1);
  const TICK_COUNT = 10;

  const roughStep = rawMax / TICK_COUNT;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const niceStep = Math.ceil(roughStep / magnitude) * magnitude;
  const niceMax = niceStep * TICK_COUNT;

  return { max: niceMax, tickAmount: TICK_COUNT, stepSize: niceStep };
};

/** "20261001 - 20261008" -> ["20261001", "20261008"] */
const splitPeriod = (period: string): [string, string] => {
  const [start = "", end = ""] = period.split("-").map((p) => p.trim());
  return [start, end];
};

const Home: React.FC = () => {
  const [summary, setSummary] = useState<SummaryDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [premiumChartData, setPremiumChartData] = useState<ChartState | null>(
    null
  );
  const [contractsData, setContractsData] = useState<ChartState | null>(null);
  const [premiumYAxis, setPremiumYAxis] = useState<YAxisConfig | null>(null);
  const [contractsYAxis, setContractsYAxis] = useState<YAxisConfig | null>(
    null
  );
  const [isChartLoading, setIsChartLoading] = useState(true);
  const [xAxisCategories, setXAxisCategories] = useState<string[]>([]);

  const { jwt } = useAuth();

  // Summary cards - fetched fresh on mount / login
  useEffect(() => {
    let cancelled = false;

    const getSummary = async () => {
      try {
        setIsLoading(true);
        const summaryData = normalizeSummaryReportProperties(
          (await OfficeService.getOfficeStatistics(jwt)) as SummaryDetails
        );
        if (!cancelled) setSummary(summaryData);
      } catch (error) {
        console.error("Грешка при зареждане на данни:", error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    getSummary();
    return () => {
      cancelled = true;
    };
  }, [jwt]);

  // Charts - fetched fresh on mount / login
  useEffect(() => {
    let cancelled = false;

    const fetchChartData = async () => {
      try {
        setIsChartLoading(true);

        const response = CapitalizationUtil.convertKeysToCamelCase(
          await OfficeService.getOfficeChartStatistic(jwt)
        ) as Record<string, unknown>;

        // Keep only the period rows (drops "Time" etc.)
        const rows = (Object.values(response) as unknown[]).filter(
          (value): value is ChartRow =>
            typeof value === "object" &&
            value !== null &&
            typeof (value as ChartRow).period === "string"
        );

        // API returns newest -> oldest. Sort oldest -> newest by period start
        // (YYYYMMDD strings sort correctly lexicographically).
        rows.sort((a, b) =>
          splitPeriod(a.period)[0].localeCompare(splitPeriod(b.period)[0])
        );

        // X-axis labels are derived from the same rows, so they always match
        // the data (includes the current, partial month).
        const newXAxisCategories = rows.map((row) =>
          moment(splitPeriod(row.period)[0], "YYYYMMDD").format("MM/YYYY")
        );

        const categories = rows.map((row) => {
          const [start, end] = splitPeriod(row.period);
          return `${normalizeDate(start)} - ${normalizeDate(end)}`;
        });

        const premiums = rows.map((row) => Number(row.premium));
        const contracts = rows.map((row) => Number(row.newContracts));

        if (cancelled) return;

        setPremiumChartData({
          series: [{ name: "Премии", data: premiums }],
          categories,
        });
        setContractsData({
          series: [{ name: "Нови договори", data: contracts }],
          categories,
        });
        setXAxisCategories(newXAxisCategories);
        setPremiumYAxis(computeYAxisConfig(premiums));
        setContractsYAxis(computeYAxisConfig(contracts));
      } catch (error) {
        console.error("Грешка при зареждане на диаграмата:", error);
        if (!cancelled) {
          setPremiumChartData(null);
          setContractsData(null);
        }
      } finally {
        if (!cancelled) setIsChartLoading(false);
      }
    };

    fetchChartData();
    return () => {
      cancelled = true;
    };
  }, [jwt]);

  const defaultSummary: Partial<SummaryDetails> = {
    count: "0",
    newContracts: "0",
  };

  return (
    <>
      <div className="2xl:gap-7.5 grid grid-cols-1 gap-4 dark:text-white md:grid-cols-2 md:gap-6 xl:grid-cols-4">
        {summary &&
          Object.entries(summary).map(([key, value]) =>
            key !== "time" ? (
              <CardDataStats
                key={key}
                loading={isLoading}
                title={SUMMARY_MAPPING_ENUM[key as keyof SummaryDetails]}
                total={value}
                icon={SUMMARY_ICON_MAPPING_ENUM[key as keyof SummaryDetails]}
              />
            ) : null
          )}
        {!summary &&
          Object.entries(defaultSummary).map(([key, value]) =>
            key !== "time" ? (
              <CardDataStats
                key={key}
                loading={isLoading}
                title={SUMMARY_MAPPING_ENUM[key as keyof SummaryDetails]}
                total={value}
                icon={SUMMARY_ICON_MAPPING_ENUM[key as keyof SummaryDetails]}
              />
            ) : null
          )}
      </div>
      <div className="2xl:mt-7.5 2xl:gap-7.5 mt-4 grid grid-cols-12 gap-4 md:mt-6 md:gap-6">
        <PremiumsChart
          chartData={premiumChartData}
          loading={isChartLoading}
          xAxisCategories={xAxisCategories}
          yAxisConfig={premiumYAxis}
        />
      </div>
      <div className="2xl:mt-7.5 2xl:gap-7.5 mt-4 grid grid-cols-12 gap-4 md:mt-6 md:gap-6">
        <NewContractsChart
          chartData={contractsData}
          loading={isChartLoading}
          xAxisCategories={xAxisCategories}
          yAxisConfig={contractsYAxis}
        />
      </div>
    </>
  );
};

export default Home;
